import type { components } from "@oathera/contract/api";
import type { Measure } from "../../components/index.ts";
import type { EnrolmentFunnel } from "./use-identity.ts";

/**
 * Presentation vocabulary for the Identity lifecycle screen (task 11.7; design 12.2 Identity row,
 * 12.3 principles). It keeps the one-way maps from contract shapes onto the screen's presentational
 * pieces in a single, testable place and defines NO API shapes (those come from
 * `@oathera/contract/api` via `use-identity.ts`):
 *
 * - how a `Quantity`'s exactness becomes an exact/estimate/unverifiable `Measure` (design 12.3);
 * - human labels for enrolment stages, token kinds/outcomes/denial reasons, and lifecycle actions;
 * - how a count/conversion/duration formats honestly (null → "n/a", durations as human spans);
 * - the stuck-pending derivation helpers that make the THREE named behaviours (FR-4.1) explicit and
 *   testable: stuck-pending enrolments remain non-converted funnel entries, are excluded from the
 *   completed time-to-complete statistics, and show their current age;
 * - the drill-down `/events` query strings for funnel stages, token denial reasons and lifecycle
 *   events (design 12.3 "numbers drill down … with matching filters").
 */

type Quantity = components["schemas"]["Quantity"];
type EnrollmentStage = components["schemas"]["enum_enrollment_stage"];
type TokenKind = components["schemas"]["enum_token_kind"];
type TokenOutcome = components["schemas"]["enum_token_outcome"];
type DenialReason = components["schemas"]["enum_denial_reason"];
type LifecycleAction = components["schemas"]["LifecycleEntry"]["action"];
type ActorType = NonNullable<components["schemas"]["LifecycleEntry"]["actor_type"]>;

/** Map a `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3). */
export function quantityMeasure(q: Quantity): Measure {
  // No data cannot be verified: neither an exact counter nor a sampled estimate, so never green.
  if (q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/* ---- Enrolment funnel ------------------------------------------------------------------------ */

/** The four funnel stages in order (design 12.2: start → poll → activated → bound). */
export const FUNNEL_ORDER: readonly EnrollmentStage[] = ["start", "poll", "activated", "bound"];

/** Human label for an enrolment stage (also used for terminal failures). */
export function stageLabel(stage: EnrollmentStage): string {
  switch (stage) {
    case "start":
      return "Started";
    case "poll":
      return "Polling";
    case "activated":
      return "Activated";
    case "bound":
      return "Bound";
    case "denied":
      return "Denied";
    case "rejected":
      return "Rejected";
  }
}

/** Format a conversion ratio (0..1) as a percentage, or "n/a" when the server could not compute it. */
export function formatConversion(ratio: number | null): string {
  if (ratio === null) return "n/a";
  return `${(ratio * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

/* ---- Stuck-pending: the THREE named behaviours (FR-4.1), made explicit and testable ----------- */

/**
 * The stuck-pending derivation, kept in one testable place so the three contract behaviours are
 * unmistakable in the UI (design 11 / 12.2 Identity, FR-4.1):
 *
 * 1. NON-CONVERTED: stuck-pending enrolments are a SUBSET of the funnel's `non_converted` count (the
 *    contract says `non_converted` counts enrolments that did not reach `bound`, "stuck-pending
 *    included"). So they count AGAINST conversion, never toward it. `nonConverted` is read straight
 *    from the funnel; `stuckAmongNonConverted` reports how many of those non-converted entries are
 *    the stuck ones, so the UI can state the relationship rather than imply it.
 * 2. EXCLUDED FROM COMPLETED TIME-TO-COMPLETE: the `completed` block (count + p50/p95 time-to-
 *    complete) is, by contract, "Only completed enrolments. Stuck-pending enrolments are excluded".
 *    `completedExcludesStuckPending` is a constant `true` that documents and lets a test assert the
 *    screen never mixes stuck ages into those duration stats — the screen reads `completed` as-is and
 *    renders the stuck ages from a DIFFERENT field (`stuck_pending`), so the two can never merge.
 * 3. CURRENT AGE: each stuck entry carries `age_s` (its current age at `meta.generated_at`), and the
 *    block carries `oldest_age_s`; `stuckAges` surfaces both so the UI shows how long each has been
 *    stuck.
 */
export interface StuckPendingView {
  /** The whole funnel's non-converted count (stuck-pending included) — behaviour 1. */
  readonly nonConverted: number;
  /** How many stuck-pending enrolments there are (a subset of `nonConverted`) — behaviour 1. */
  readonly stuckCount: number;
  /** Always true: the completed time-to-complete stats exclude stuck-pending — behaviour 2. */
  readonly completedExcludesStuckPending: true;
  /** The staleness threshold (seconds) above which a pending enrolment is "stuck". */
  readonly thresholdS: number;
  /** The oldest stuck age in seconds, or null when there are none / unknown — behaviour 3. */
  readonly oldestAgeS: number | null;
  /** Per-entry current ages, newest-stuck last — behaviour 3. */
  readonly items: EnrolmentFunnel["stuck_pending"]["items"];
}

/** Derive the stuck-pending view from the funnel, making the three behaviours explicit. */
export function deriveStuckPending(funnel: EnrolmentFunnel): StuckPendingView {
  const stuck = funnel.stuck_pending;
  return {
    nonConverted: funnel.non_converted,
    stuckCount: stuck.count,
    completedExcludesStuckPending: true,
    thresholdS: stuck.threshold_s,
    oldestAgeS: stuck.oldest_age_s,
    items: stuck.items,
  };
}

/* ---- Durations -------------------------------------------------------------------------------- */

/** Format a millisecond duration as a short human span (e.g. "1.2 s", "3 m 05 s", "2 h 14 m"). */
export function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "n/a";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const totalS = ms / 1000;
  if (totalS < 60) return `${totalS.toLocaleString(undefined, { maximumFractionDigits: 1 })} s`;
  return formatDurationSeconds(totalS);
}

/** Format a seconds duration as a short human span (used for stuck-pending ages). */
export function formatDurationSeconds(totalS: number): string {
  if (!Number.isFinite(totalS) || totalS < 0) return "n/a";
  const s = Math.floor(totalS);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} m ${String(s % 60).padStart(2, "0")} s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${String(m % 60).padStart(2, "0")} m`;
  const d = Math.floor(h / 24);
  return `${d} d ${String(h % 24).padStart(2, "0")} h`;
}

/** Format a `Quantity` whose unit is milliseconds (the time-to-complete percentiles). */
export function formatDurationQuantity(q: Quantity): string {
  if (q.value === null) return "n/a";
  return formatDurationMs(q.value);
}

/* ---- Tokens ----------------------------------------------------------------------------------- */

/** Human label for a token kind. */
export function tokenKindLabel(kind: TokenKind): string {
  return kind === "agent" ? "Agent" : "Federation";
}

/** Human label for a token outcome. */
export function tokenOutcomeLabel(outcome: TokenOutcome): string {
  return outcome === "issued" ? "Issued" : "Denied";
}

/** Human label for a denial reason (shared with enrolment denials; `none` means "no denial"). */
export function denialReasonLabel(reason: DenialReason): string {
  switch (reason) {
    case "none":
      return "No denial";
    case "policy_denied":
      return "Policy denied";
    case "owner_not_eligible":
      return "Owner not eligible";
    case "agent_unknown":
      return "Agent unknown";
    case "agent_revoked":
      return "Agent revoked";
    case "agent_suspended":
      return "Agent suspended";
    case "host_untrusted":
      return "Host untrusted";
    case "audience_not_allowed":
      return "Audience not allowed";
    case "version_unsupported":
      return "Version unsupported";
    case "rate_limited":
      return "Rate limited";
    case "dependency_unavailable":
      return "Dependency unavailable";
    case "other":
      return "Other";
  }
}

/* ---- Lifecycle -------------------------------------------------------------------------------- */

/** Human label for a lifecycle action (including the synthetic `epoch_change`). */
export function lifecycleActionLabel(action: LifecycleAction): string {
  switch (action) {
    case "revoke":
      return "Revoke";
    case "suspend":
      return "Suspend";
    case "reinstate":
      return "Reinstate";
    case "deprovision":
      return "Deprovision";
    case "delete":
      return "Delete";
    case "anchor_rotate":
      return "Anchor rotation";
    case "epoch_change":
      return "Epoch change";
    default:
      // The action set is provisional in the contract; show the raw token rather than hide it.
      return String(action);
  }
}

/** Human label for who performed a lifecycle action. */
export function actorTypeLabel(actor: ActorType | null): string {
  if (actor === null) return "Unknown";
  switch (actor) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "system":
      return "System";
    case "agent":
      return "Agent";
    case "other":
      return "Other";
    default:
      return String(actor);
  }
}

/* ---- Drill-down to /events -------------------------------------------------------------------- */

/**
 * Build a relative `/events` link from a set of filter params. Values are encoded by
 * `URLSearchParams`; empty values are dropped. The identity endpoints do not attach a server-built
 * `drill` (unlike the decisions breakdown), so the screen composes honest standing filters from real
 * `/events` params — never an invented route. The events screen (task 11.12) reads these params.
 */
function eventsHref(params: Readonly<Record<string, string>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value.length > 0) search.set(key, value);
  }
  const qs = search.toString();
  return qs.length > 0 ? `/events?${qs}` : "/events";
}

/**
 * The `/events` filter for the enrolments that reached a given funnel stage. `category=enrolment`
 * plus the stage are faithful standing filters, so the funnel's per-stage counts drill through.
 */
export function funnelStageHref(stage: EnrollmentStage): string {
  return eventsHref({ category: "enrolment", stage });
}

/** The `/events` filter for a terminal enrolment failure (denied / rejected). */
export function terminalFailureHref(stage: EnrollmentStage): string {
  return eventsHref({ category: "enrolment", stage });
}

/** The `/events` filter for the stuck-pending enrolments (pending past the threshold). */
export function stuckPendingHref(): string {
  return eventsHref({ category: "enrolment", status: "stuck_pending" });
}

/** The `/events` filter for a token denial reason (so a denial breakdown row drills through). */
export function tokenDenialHref(reason: DenialReason): string {
  return eventsHref({ category: "token", outcome: "denied", reason });
}

/** The `/events` filter for a lifecycle action, optionally scoped to one agent. */
export function lifecycleActionHref(action: LifecycleAction, agent: string | null): string {
  const base: Record<string, string> = { category: "lifecycle", action: String(action) };
  if (agent !== null) base["agent"] = agent;
  return eventsHref(base);
}
